<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	//Design system
	import { Alert, Button, Loader } from '@Shelly/shelly-design-system/index';
	//Components
	import IrrigationCard from './IrrigationCard.svelte';
	import IrrigationZoneController from './IrrigationZoneController.svelte';
	import IrrigationConfirmModal from './IrrigationConfirmModal.svelte';
	// Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Sequence, VCInterfaceStatus } from 'src/types/VirtualComponents';

	export let device: Device, sequences: VCInterfaceStatus, zones: VCInterfaceStatus[];

	let waitForResponse = false;
	let responseError = '';
	let irrigationSequences: Sequence[] = null;
	let activeSequence: Sequence = null;
	let confirmModal: IrrigationConfirmModal;

	const getListSequences = async () => {
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.ListSequences', { id: 0 });
		if (res?.isok) {
			//@ts-ignore
			irrigationSequences = res?.sequences || res?.data?.sequences || [];
			activeSequence = irrigationSequences.find(
				(s) => s.seq_id === Number(sequences?.event.split('_')[1]),
			);
		} else {
			responseError = $format('alert_danger_options_active_zone_error', {
				default: 'Could not load the active zones list.',
			});
		}
		waitForResponse = false;
	};

	const getSequencesDuration = (activeSequence: Sequence) => {
		let duration = 0;
		activeSequence.zones.forEach((z) => {
			duration += zones[z].timer.duration;
		});
		return duration;
	};

	const stopSequence = async () => {
		if (!(await confirmModal.confirm())) return;
		await gen2Request(device, 'enum.set', { id: 200, value: 'none' });
	};

	getListSequences();
</script>

<IrrigationCard
	title={$format('irrigation_active_sequence_title', {
		default: 'Active Sequence',
	})}
	icon="far fa-network-wired"
>
	{#if waitForResponse}
		<div class="d-flex justify-content-center">
			<Loader size="m" class="mt-m align-self-center" />
		</div>
	{:else if responseError}
		<Alert class="mt-m mb-s" type="danger" heading={responseError} />
		<Button icon="fas fa-arrows-rotate" size="m" class="width-full" on:click={getListSequences}>
			{$format('button_rescan', { default: 'Rescan' })}
		</Button>
	{:else if activeSequence}
		<div class="sequence-item">
			<div class="sequence-info">
				<div class="sequence-name">{activeSequence.name}</div>
				<div class="sequence-duration">
					<i class="fas fa-timer"></i>
					<span
						>{$format('text_trv_setting_duration', {
							default: 'Duration',
						})}
						{getSequencesDuration(activeSequence) +
							' ' +
							$format('irrigation_labellist_value_min', {
								default: 'min',
							})}</span
					>
				</div>
			</div>
			<div class="sequence-controls">
				<i
					class="fat fa-stop"
					style="cursor: pointer;"
					role="presentation"
					on:click={() => stopSequence()}
				></i>
				<i
					class="fat fa-cog"
					style="cursor: pointer;"
					role="presentation"
					on:click={() => navigateToSetting(device.id, 'irrigation_sequences')}
				></i>
			</div>
		</div>
		<div class="sequence-zones-container">
			<div class="zones-line"></div>
			{#each activeSequence.zones as zoneID}
				{@const zone = zones[zoneID]}
				<IrrigationZoneController
					{device}
					{zone}
					showOnlyButton
					activeZones={activeSequence?.zones}
					activeZone={activeSequence?.zones?.indexOf(zones.find((z) => z.event)?.id - 200)}
				/>
			{/each}
		</div>
	{/if}
</IrrigationCard>
{#each zones as zone}
	<IrrigationZoneController
		{device}
		{zone}
		activeZones={activeSequence?.zones}
		activeZone={activeSequence?.zones?.indexOf(zones.find((z) => z.event)?.id - 200)}
	/>
{/each}

<IrrigationConfirmModal bind:this={confirmModal} />

<style>
	.sequence-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 16px;
		background: transparent;
		border-radius: 8px;
		margin-bottom: 16px;
		border: 1px solid #3a3f3f;
	}
	.sequence-info {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}
	.sequence-name {
		color: white;
		font-size: 14px;
	}
	.sequence-duration {
		display: flex;
		align-items: center;
		gap: 4px;
		color: #8e8e93;
		font-size: 12px;
	}
	.sequence-controls {
		display: flex;
		align-items: center;
		gap: 12px;
	}
	.sequence-zones-container {
		position: relative;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0 16px;
		margin-top: 16px;
	}

	.zones-line {
		position: absolute;
		top: 50%;
		left: 16px;
		right: 16px;
		height: 2px;
		background: #2c2c2e;
		z-index: 1;
		transform: translateY(-50%);
	}
</style>
